import dayjs from 'dayjs';
import { MetaAdsCampaign } from '../components/apis/metaAdsApi';

export interface CampaignVideo {
  id: string;
  title: string;
}

/**
 * Format a money amount with its currency code, e.g. formatMoney(55, "PLN") => "PLN 55.00".
 * Currency is the ad-account currency (ISO code); defaults to "USD" when unknown.
 */
export const formatMoney = (amount: number, currency?: string): string =>
  `${currency || "USD"} ${amount.toFixed(2)}`;

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Has a scheduled campaign's end date/time already passed?
 *
 * rawEndDate is either a plain "YYYY-MM-DD" (our own draft schedule, no time
 * component - the whole day still counts as running, so this end is treated
 * as end-of-day) or a full ISO timestamp with an explicit offset (Meta's
 * stop_time, e.g. "2026-08-07T23:59:00+0300" - already a precise instant).
 *
 * Only the date-only form should get `.endOf("day")` applied. Doing that to
 * an already-precise timestamp re-derives "end of day" in the BROWSER's local
 * timezone, which can roll the date forward a full day before flooring -
 * delaying "ended" by up to 24h depending on the viewer's timezone relative
 * to the campaign's configured one.
 */
export const isScheduledCampaignEnded = (
  rawEndDate?: string | null,
  now: dayjs.Dayjs = dayjs(),
): boolean => {
  if (!rawEndDate) return false;

  const parsed = DATE_ONLY_PATTERN.test(rawEndDate)
    ? dayjs(rawEndDate).endOf("day")
    : dayjs(rawEndDate);

  return parsed.isValid() && parsed.isBefore(now);
};

/** Inclusive day count between two dates, e.g. 02 Jun -> 02 Jun is 1 day. */
const inclusiveDays = (from: dayjs.Dayjs, to: dayjs.Dayjs): number =>
  to.startOf("day").diff(from.startOf("day"), "day") + 1;

/**
 * Total budget for a scheduled campaign.
 *
 * Money already spent is sunk - only the days still to come are charged at the
 * new daily rate. So changing the daily budget mid-flight does NOT retroactively
 * reprice the days that already ran:
 *
 *   10-day campaign at 10/day, 4 days in (40 spent), raised to 20/day
 *   -> 40 + (6 remaining x 20) = 160,  not  10 x 20 = 200
 *
 * Today is a special case: if it has already spent more than the new rate, that
 * spend can't be undone either - lowering the daily budget only limits what's
 * left to spend today, not what already went out. So today bills at whichever
 * is larger, its actual spend or the new rate, not the new rate unconditionally:
 *
 *   10/day campaign, day 1 spends 10, midway through day 2 spends 6 more (16
 *   total), budget lowered to 5/day -> today bills max(6, 5) = 6, not 5
 *   -> 10 (day 1) + 6 (today, already over the new cap) + 8 remaining x 5 = 56
 *
 * `spent` of null/undefined means "not loaded yet" and falls back to the whole
 * duration at the new rate, so a missing figure never under-reports the budget.
 * `spentToday` of null/undefined (today's figure not loaded, or today isn't a
 * running day of the campaign) skips the today-specific split above and falls
 * back to billing every remaining day - including today - at the new rate.
 */
export function calculateScheduledBudgetTotal({
  dailyAmount,
  startDate,
  endDate,
  spent,
  spentToday,
  now = dayjs(),
}: {
  dailyAmount: number;
  startDate?: string | null;
  endDate?: string | null;
  spent?: number | null;
  spentToday?: number | null;
  now?: dayjs.Dayjs;
}): number | null {
  if (!dailyAmount || !startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return null;

  const totalDays = inclusiveDays(start, end);
  if (totalDays <= 0) return null;

  if (spent === null || spent === undefined) return dailyAmount * totalDays;

  // Days still to be charged: today through the end date, inclusive. Clamped so a
  // not-yet-started campaign bills its full run and an ended one bills nothing more.
  const remainingDays = Math.min(
    Math.max(inclusiveDays(now, end), 0),
    totalDays,
  );

  const today = now.startOf("day");
  const isRunning = !today.isBefore(start, "day") && !today.isAfter(end, "day");

  if (!isRunning || spentToday === null || spentToday === undefined) {
    return spent + dailyAmount * remainingDays;
  }

  const remainingFutureDays = remainingDays - 1; // strictly after today
  const todayContribution = Math.max(spentToday, dailyAmount);

  return spent + todayContribution + dailyAmount * remainingFutureDays;
}

/** "EUR 160.00 (EUR 20.00/day)", or "EUR 20.00/day" when there is no end date. */
export function formatScheduledBudget({
  dailyAmount,
  currency,
  startDate,
  endDate,
  spent,
  spentToday,
  now,
}: {
  dailyAmount: number;
  currency?: string;
  startDate?: string | null;
  endDate?: string | null;
  spent?: number | null;
  spentToday?: number | null;
  now?: dayjs.Dayjs;
}): string {
  const total = calculateScheduledBudgetTotal({
    dailyAmount,
    startDate,
    endDate,
    spent,
    spentToday,
    ...(now && { now }),
  });

  if (total === null) return `${formatMoney(dailyAmount, currency)}/day`;

  return `${formatMoney(total, currency)} (${formatMoney(dailyAmount, currency)}/day)`;
}

export interface CampaignCardData {
  id: string;
  name: string;
  status: "Active" | "Paused" | "Draft";
  conversionPage: string;
  startDate: string;
  endDate: string;
  rawStartDate?: string | null;
  rawEndDate?: string | null;
  isScheduled?: boolean;
  budget: string;
  currency?: string; // Ad-account currency code (e.g. "PLN") for the budget display
  engagement: string;
  visits: string;
  clicks: string;
  cost: string;
  results: {
    clicks: string;
    ctr: string;
    conversions: string;
  };
  lastUpdated: string;
  // Additional fields for campaigns from Meta
  isFromMeta?: boolean;
  isDraft?: boolean;
  objective?: string;
  promoteType?: "track" | "playlist";
  accountId?: string;
  createdAt?: string;
  updatedAt?: string;
  linkUrls?: string[];
  videos?: CampaignVideo[];
  trackInfo?: {
    linkUrl?: string;
    smartLink?: string;
    artistName?: string;
    songName?: string;
  };
  performance?: {
    dateRange: string;
    reach: number;
    impressions: number;
    clicks: number;
    spend: number;
    spendThroughYesterday?: number;
    asOfDate?: string;
    conversions: number;
  };
}

export function transformMetaCampaignToCard(
  campaign: MetaAdsCampaign
): CampaignCardData {
  // Format status
  const status =
    campaign.status === "ACTIVE"
      ? ("Active" as const)
      : campaign.status === "PAUSED"
      ? ("Paused" as const)
      : ("Active" as const);

  // Format dates
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "None";
    return dayjs(dateStr).format("DD MMM YYYY");
  };

  // Calculate budget string
  const getBudgetString = () => {
    const { type, amount } = campaign.budget;

    if (!amount) return "Not set";

    // For DAILY budget, show amount/day
    // For SCHEDULED budget, also show amount/day
    const dailyStr = `€${amount}/day`;

    // Estimate monthly budget (30 days)
    const total = amount * 30;
    const totalStr = `€${total} total`;

    if (type === "SCHEDULED") {
      return `${totalStr} (${dailyStr}) - Scheduled`;
    } else {
      return `${totalStr} (${dailyStr}) - Always Active`;
    }
  };

  // Safely access schedule properties
  const schedule = campaign.schedule || {
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    timezone: null,
    isScheduled: false,
  };

  // Format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Format last updated
  const formatLastUpdated = (date?: string) => {
    if (!date) return "Today, 14:32"; // Default fallback
    const now = dayjs();
    const updated = dayjs(date);
    const diffHours = now.diff(updated, 'hour');

    if (diffHours < 24) {
      return `Today, ${updated.format("HH:mm")}`;
    } else if (diffHours < 48) {
      return "Yesterday";
    } else {
      return updated.format("DD MMM YYYY");
    }
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return {
    id: campaign._id,
    name: campaign.campaignName,
    status,
    conversionPage: "Smart link / Landing page",
    startDate: formatDate(schedule.startDate),
    endDate: formatDate(schedule.endDate),
    budget: getBudgetString(),
    engagement: campaign.performance.ctr
      ? formatPercentage(campaign.performance.ctr)
      : "N/A",
    visits: formatNumber(campaign.performance.impressions),
    clicks: formatNumber(campaign.performance.clicks),
    cost: campaign.performance.cpc
      ? `€${campaign.performance.cpc.toFixed(2)}`
      : "N/A",
    results: {
      clicks: `${formatNumber(campaign.performance.clicks)} clicks`,
      ctr: campaign.performance.ctr
        ? `${formatPercentage(campaign.performance.ctr)} CTR`
        : "N/A",
      conversions: "N/A", // Not available in backend yet
    },
    lastUpdated: formatLastUpdated(
      campaign.performance.lastUpdated || campaign.updatedAt
    ),
  };
}
